import { BrowserRouter, Route, Routes } from "react-router-dom";
import "./App.css";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Browse from "./pages/Browse";
import ListingDetails from "./pages/ListingDetails";
import CreateListing from "./pages/CreateListing";
import Profile from "./pages/Profile";
import { AuthProvider } from "./contexts/AuthContext";
import EditProfile from "./pages/EditProfile";
import EditLisitng from "./pages/EditListing";
import Messages from "./pages/Messages";
import RatingModal from "./components/RatingModal";
import Resources from "./pages/Resources";
import Settings from "./pages/Settings";
import UserProfile from "./pages/UserProfile";

// Admin
import AdminLayout from "./admin/AdminLayout";
import Dashboard from "./admin/pages/Dashboard";
import ChatMonitor from "./admin/pages/ChatMonitor";
import UserManagement from "./admin/pages/UserManagement";
import Report from "./admin/pages/Report";
import Categories from "./admin/pages/Categories";
import AdminLogin from "./admin/pages/AdminLogin";
import { FilterProvider } from "./contexts/FilterContext";
import { AdminAuthProvider } from "./admin/context/adminAuth";
import AdminProtectedRoute from "./admin/components/AdminProtectedRoute";
import { ChatProvider } from "./contexts/ChatContext";
import { useRatingModal } from "./hooks/useRatingModal";
import { useEffect } from "react";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import EmailVerification from "./pages/EmailVerification";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AboutUs from "./pages/AboutUs";
import ManageResources from "./admin/pages/ManageResources";
import ResourcePreview from "./admin/pages/ResourcePreview";

function App() {
  const {
    isModalOpen,
    pendingExchange,
    handleSubmitRating,
    handleRemindLater,
    handleCloseModal,
    checkForPendingRatings,
  } = useRatingModal();

  // Check for pending ratings when the app loads
  useEffect(() => {
    // Check for pending ratings on app load
    checkForPendingRatings();

    // Optional: Check periodically (every 5 minutes)
    const interval = setInterval(checkForPendingRatings, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkForPendingRatings]);

  // Check for pending ratings when the user comes back to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForPendingRatings();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [checkForPendingRatings]);

  return (
    <div className="App">
      <AuthProvider>
        <FilterProvider>
          <ChatProvider>
            <AdminAuthProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/aboutus" element={<AboutUs />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/browse" element={<Browse />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/create-listing" element={<CreateListing />} />
                  <Route path="/listing/:id" element={<ListingDetails />} />
                  <Route path="/listing/:id/edit" element={<EditLisitng />} />
                  <Route path="/edit-profile" element={<EditProfile />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/profile/:userId" element={<UserProfile />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/verify-email/:token" element={<EmailVerification />} />
                  <Route path="/resources" element={<Resources />} />

                  {/* Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <AdminProtectedRoute>
                        <AdminLayout />
                      </AdminProtectedRoute>
                    }
                  >
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="chat" element={<ChatMonitor />} />
                    <Route path="categories" element={<Categories />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="reports" element={<Report />} />
                    <Route path="resources" element={<ManageResources />} />
                    <Route path="resources/:id" element={<ResourcePreview />} />
                    <Route index element={<Dashboard />} /> {/* default */}
                  </Route>

                  {/* Admin login (not protected) */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                </Routes>

                {/* Rating Modal - appears on top of any page - MOVED OUTSIDE Routes */}
                <RatingModal
                  isOpen={isModalOpen}
                  onClose={handleCloseModal}
                  exchange={pendingExchange}
                  onSubmitRating={handleSubmitRating}
                  onRemindLater={handleRemindLater}
                />
              </BrowserRouter>
            </AdminAuthProvider>
          </ChatProvider>
        </FilterProvider>
      </AuthProvider>
    </div>
  );
}

export default App;